import React, { useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useActiveClinic } from "../context/ClinicContext"
import { apiFetch } from "../utils/apiFetch"
import "../shared/styles/settings-system.css"

const API_BASE =
  (typeof window !== "undefined" &&
  ["localhost", "127.0.0.1", "0.0.0.0"].includes(window.location.hostname)
    ? ["http://localhost", "3000/api"].join(":")
    : "/api")

const SPECIALTIES = [
  { value: "", label: "General / Sin restricciÃ³n" },
  { value: "GENERAL", label: "General" },
  { value: "ORTODONCIA", label: "Ortodoncia" },
  { value: "ENDODONCIA", label: "Endodoncia" },
  { value: "CIRUGIA", label: "CirugÃ­a" },
  { value: "IMPLANTOLOGIA", label: "ImplantologÃ­a" },
  { value: "PROSTODONCIA", label: "Prostodoncia" },
  { value: "PERIODONCIA", label: "Periodoncia" },
  { value: "PEDIATRIA", label: "PediatrÃ­a" }
]

const ODONTO_CONDITIONS = [
  "",
  "CARIES",
  "RESTORATION",
  "ENDODONTIC",
  "FRACTURE",
  "EXTRACTED",
  "IMPLANT",
  "HEALTHY"
]

function formatMoney(value) {
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    maximumFractionDigits: 0
  }).format(Number(value || 0))
}

function SimpleTable({ headers, rows, emptyLabel }) {
  if (!rows.length) {
    return <div className="of-settings-muted">{emptyLabel}</div>
  }

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            {headers.map((header, index) => (
              <th
                key={`${header}-${index}`}
                style={{
                  textAlign: "left",
                  fontSize: 12,
                  color: "#64748b",
                  borderBottom: "1px solid #e2e8f0",
                  padding: "10px 8px",
                  whiteSpace: "nowrap"
                }}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={idx}>
              {row.map((cell, cellIdx) => (
                <td
                  key={cellIdx}
                  style={{
                    padding: "12px 8px",
                    borderBottom: "1px solid #f1f5f9",
                    color: "#334155",
                    fontSize: 14,
                    verticalAlign: "top"
                  }}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default function TreatmentCatalogPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const { activeClinicId } = useActiveClinic()

  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  )

  const treatmentIdFromUrl = queryParams.get("treatmentId") || ""
  const searchFromUrl = queryParams.get("q") || ""

  const clinicId = useMemo(() => {
    if (activeClinicId === "ALL") return ""
    return activeClinicId || ""
  }, [activeClinicId])

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [editingId, setEditingId] = useState("")
  const [search, setSearch] = useState(searchFromUrl)
  const [rows, setRows] = useState([])

  const [form, setForm] = useState({
    name: "",
    durationMinutes: "30",
    price: "",
    requiredSpecialty: "",
    recallDays: "",
    odontogramCondition: ""
  })

  const [editForm, setEditForm] = useState({
    id: "",
    name: "",
    durationMinutes: "",
    price: "",
    requiredSpecialty: "",
    recallDays: "",
    odontogramCondition: ""
  })

  const loadTreatments = async () => {
    try {
      if (!clinicId) {
        setRows([])
        setLoading(false)
        return
      }

      setLoading(true)

      const params = new URLSearchParams({ clinicId })
      if (search.trim()) params.set("q", search.trim())

      const res = await apiFetch(`${API_BASE}/treatments?${params.toString()}`)
      const data = await res.json().catch(() => [])

      setRows(res.ok && Array.isArray(data) ? data : [])
    } catch {
      setRows([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (searchFromUrl) {
      setSearch(searchFromUrl)
    }
  }, [searchFromUrl])

  useEffect(() => {
    loadTreatments()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicId])

  useEffect(() => {
    const t = setTimeout(() => {
      loadTreatments()
    }, 250)

    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  const createTreatment = async () => {
    try {
      if (!clinicId) {
        alert("Selecciona una clÃ­nica para crear tratamientos.")
        return
      }

      if (!form.name.trim()) {
        alert("Escribe el nombre del tratamiento.")
        return
      }

      setSaving(true)

      const res = await apiFetch(`${API_BASE}/treatments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          clinicId,
          name: form.name.trim(),
          durationMinutes: Number(form.durationMinutes || 0),
          price: Number(form.price || 0),
          requiredSpecialty: form.requiredSpecialty || null,
          recallDays: form.recallDays ? Number(form.recallDays) : null,
          odontogramCondition: form.odontogramCondition || null
        })
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        alert(data?.message || "No se pudo crear el tratamiento.")
        return
      }

      setForm({
        name: "",
        durationMinutes: "30",
        price: "",
        requiredSpecialty: "",
        recallDays: "",
        odontogramCondition: ""
      })

      await loadTreatments()
      alert("Tratamiento creado correctamente.")
    } catch {
      alert("Error al crear tratamiento.")
    } finally {
      setSaving(false)
    }
  }

  const openMappingForTreatment = (row) => {
    if (row?.odontogramCondition) {
      navigate(`/odontogram-mappings?condition=${encodeURIComponent(row.odontogramCondition)}`)
      return
    }

    navigate("/odontogram-mappings")
  }

  useEffect(() => {
    if (!treatmentIdFromUrl) return
    if (!rows.length) return

    const row = rows.find((item) => String(item.id) === String(treatmentIdFromUrl))
    if (!row) return

    setEditingId(row.id)
    setEditForm({
      id: row.id,
      name: row.name || "",
      durationMinutes: String(row.durationMinutes || ""),
      price: String(row.price || ""),
      requiredSpecialty: row.requiredSpecialty || "",
      recallDays: row.recallDays != null ? String(row.recallDays) : "",
      odontogramCondition: row.odontogramCondition || ""
    })
  }, [treatmentIdFromUrl, rows])

  const startEdit = (row) => {
    setEditingId(row.id)
    setEditForm({
      id: row.id,
      name: row.name || "",
      durationMinutes: String(row.durationMinutes || ""),
      price: String(row.price || ""),
      requiredSpecialty: row.requiredSpecialty || "",
      recallDays: row.recallDays != null ? String(row.recallDays) : "",
      odontogramCondition: row.odontogramCondition || ""
    })
  }

  const cancelEdit = () => {
    setEditingId("")
    setEditForm({
      id: "",
      name: "",
      durationMinutes: "",
      price: "",
      requiredSpecialty: "",
      recallDays: "",
      odontogramCondition: ""
    })
  }

  const ODONTOGRAM_CONDITIONS = [
    "CARIES",
    "FRACTURE",
    "MISSING",
    "ENDODONTIC",
    "RESTORATION",
    "IMPLANT",
    "CROWN",
    "BRIDGE",
    "PROSTHESIS",
    "MOBILITY",
    "CALCULUS",
    "GINGIVITIS"
  ]

  const [assigningConditionId, setAssigningConditionId] = useState(null)
  const [selectedCondition, setSelectedCondition] = useState("")
  const [savingCondition, setSavingCondition] = useState(false)

  const assignCondition = async (treatmentId) => {
    try {
      if (!selectedCondition) {
        alert("Selecciona una condiciÃ³n")
        return
      }

      if (!clinicId) {
        alert("Selecciona clÃ­nica")
        return
      }

      setSavingCondition(true)

      const res = await apiFetch(
        `${API_BASE}/treatments/assign-odontogram-condition`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            clinicId,
            treatmentId,
            condition: selectedCondition
          })
        }
      )

      if (!res.ok) {
        alert("No se pudo asignar condiciÃ³n")
        return
      }

      alert("CondiciÃ³n asignada")

      setAssigningConditionId(null)
      setSelectedCondition("")

      loadTreatments()
    } catch {
      alert("Error")
    } finally {
      setSavingCondition(false)
    }
  }

  const saveEdit = async () => {
    try {
      if (!editForm.id) return

      const res = await apiFetch(`${API_BASE}/treatments`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          id: editForm.id,
          name: editForm.name.trim(),
          durationMinutes: Number(editForm.durationMinutes || 0),
          price: Number(editForm.price || 0),
          requiredSpecialty: editForm.requiredSpecialty || null,
          recallDays: editForm.recallDays ? Number(editForm.recallDays) : null,
          odontogramCondition: editForm.odontogramCondition || null
        })
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        alert(data?.message || "No se pudo actualizar el tratamiento.")
        return
      }

      cancelEdit()
      await loadTreatments()
      alert("Tratamiento actualizado correctamente.")
    } catch {
      alert("Error al actualizar tratamiento.")
    }
  }

  return (
    <div className="of-settings-page">
      <div className="of-settings-hero">
        <h1 className="of-settings-hero__title">CatÃ¡logo de tratamientos</h1>
        <div className="of-settings-hero__subtitle">
          Administra precios, duraciÃ³n, especialidad y relaciÃ³n clÃ­nica de los tratamientos por clÃ­nica.
        </div>
      </div>

      {!clinicId ? (
        <div className="of-settings-banner of-settings-banner--warning">
          EstÃ¡s en vista global. Selecciona una clÃ­nica especÃ­fica para administrar el catÃ¡logo.
        </div>
      ) : (
        <div className="of-settings-grid of-settings-grid--2">
          <div className="of-settings-card">
            <div className="of-settings-card__title">Nuevo tratamiento</div>
            <div className="of-settings-card__subtitle">
              Crea un tratamiento base con su lÃ³gica clÃ­nica.
            </div>

            <div className="of-settings-grid">
              <div className="of-settings-field">
                <label className="of-settings-label">Nombre</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  className="of-settings-input"
                />
              </div>

              <div className="of-settings-grid--2">
                <div className="of-settings-field">
                  <label className="of-settings-label">DuraciÃ³n (min)</label>
                  <input
                    type="number"
                    value={form.durationMinutes}
                    onChange={(e) => setForm((prev) => ({ ...prev, durationMinutes: e.target.value }))}
                    className="of-settings-input"
                  />
                </div>

                <div className="of-settings-field">
                  <label className="of-settings-label">Precio</label>
                  <input
                    type="number"
                    value={form.price}
                    onChange={(e) => setForm((prev) => ({ ...prev, price: e.target.value }))}
                    className="of-settings-input"
                  />
                </div>
              </div>

              <div className="of-settings-grid--2">
                <div className="of-settings-field">
                  <label className="of-settings-label">Especialidad requerida</label>
                  <select
                    value={form.requiredSpecialty}
                    onChange={(e) => setForm((prev) => ({ ...prev, requiredSpecialty: e.target.value }))}
                    className="of-settings-select"
                  >
                    {SPECIALTIES.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="of-settings-field">
                  <label className="of-settings-label">Recall (dÃ­as)</label>
                  <input
                    type="number"
                    value={form.recallDays}
                    onChange={(e) => setForm((prev) => ({ ...prev, recallDays: e.target.value }))}
                    className="of-settings-input"
                  />
                </div>
              </div>

              <div className="of-settings-field">
                <label className="of-settings-label">CondiciÃ³n odontograma asociada</label>
                <select
                  value={form.odontogramCondition}
                  onChange={(e) => setForm((prev) => ({ ...prev, odontogramCondition: e.target.value }))}
                  className="of-settings-select"
                >
                  {ODONTO_CONDITIONS.map((item) => (
                    <option key={item} value={item}>
                      {item || "Sin asociaciÃ³n"}
                    </option>
                  ))}
                </select>
              </div>

              <div className="of-settings-actions" style={{ justifyContent: "flex-end" }}>
                <button
                  onClick={createTreatment}
                  disabled={saving || !clinicId}
                  className="of-settings-button of-settings-button--primary"
                >
                  {saving ? "Guardando..." : "Crear tratamiento"}
                </button>
              </div>
            </div>
          </div>

          <div className="of-settings-card">
            <div className="of-settings-card__title">Tratamientos de la clÃ­nica</div>
            <div className="of-settings-card__subtitle">
              Busca, edita y conecta tratamientos con el odontograma.
            </div>

            <div className="of-settings-actions" style={{ marginBottom: 12 }}>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar tratamiento..."
                className="of-settings-input"
                style={{ flex: 1 }}
              />

              {(treatmentIdFromUrl || searchFromUrl) && (
                <button
                  onClick={() => navigate("/treatment-catalog")}
                  className="of-settings-button"
                >
                  Limpiar contexto
                </button>
              )}
            </div>

            {loading ? (
              <div className="of-settings-muted">Cargando tratamientos...</div>
            ) : (
              <SimpleTable
                headers={[
                  "Nombre",
                  "DuraciÃ³n",
                  "Precio",
                  "Especialidad",
                  "Recall",
                  "CondiciÃ³n",
                  "Mapeo",
                  "AcciÃ³n"
                ]}
                rows={rows.map((row) => [
                  editingId === row.id ? (
                    <input
                      value={editForm.name}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))}
                      className="of-settings-input"
                    />
                  ) : (
                    row.name
                  ),
                  editingId === row.id ? (
                    <input
                      type="number"
                      value={editForm.durationMinutes}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, durationMinutes: e.target.value }))}
                      className="of-settings-input"
                    />
                  ) : (
                    `${row.durationMinutes || 0} min`
                  ),
                  editingId === row.id ? (
                    <input
                      type="number"
                      value={editForm.price}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, price: e.target.value }))}
                      className="of-settings-input"
                    />
                  ) : (
                    formatMoney(row.price || 0)
                  ),
                  editingId === row.id ? (
                    <select
                      value={editForm.requiredSpecialty}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, requiredSpecialty: e.target.value }))}
                      className="of-settings-select"
                    >
                      {SPECIALTIES.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    row.requiredSpecialty || "General"
                  ),
                  editingId === row.id ? (
                    <input
                      type="number"
                      value={editForm.recallDays}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, recallDays: e.target.value }))}
                      className="of-settings-input"
                    />
                  ) : (
                    row.recallDays != null ? `${row.recallDays} dÃ­as` : "-"
                  ),
                  editingId === row.id ? (
                    <select
                      value={editForm.odontogramCondition}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, odontogramCondition: e.target.value }))}
                      className="of-settings-select"
                    >
                      {ODONTO_CONDITIONS.map((item) => (
                        <option key={item} value={item}>
                          {item || "Sin asociaciÃ³n"}
                        </option>
                      ))}
                    </select>
                  ) : (
                    row.odontogramCondition || "-"
                  ),
                  row.odontogramCondition ? (
                    <span className="of-settings-pill">Vinculado</span>
                  ) : (
                    <span className="of-settings-pill">Sin mapear</span>
                  ),
                  editingId === row.id ? (
                    <div className="of-settings-actions">
                      <button
                        onClick={saveEdit}
                        className="of-settings-button of-settings-button--success"
                      >
                        Guardar
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="of-settings-button"
                      >
                        Cancelar
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: "grid", gap: 8 }}>
                      <div className="of-settings-actions">
                        <button
                          onClick={() => startEdit(row)}
                          className="of-settings-button of-settings-button--info"
                        >
                          Editar
                        </button>

                        <button
                          onClick={() => openMappingForTreatment(row)}
                          className="of-settings-button"
                        >
                          Ver mapeo
                        </button>

                        <button
                          onClick={() => {
                            setAssigningConditionId(row.id)
                            setSelectedCondition(row.odontogramCondition || "")
                          }}
                          className="of-settings-button of-settings-button--warning"
                        >
                          Mapear odontograma
                        </button>
                      </div>

                      {assigningConditionId === row.id && (
                        <div className="of-settings-card of-settings-card--soft">
                          <div className="of-settings-field">
                            <label className="of-settings-label">
                              Selecciona condiciÃ³n clÃ­nica
                            </label>

                            <select
                              value={selectedCondition}
                              onChange={(e) => setSelectedCondition(e.target.value)}
                              className="of-settings-select"
                            >
                              <option value="">Seleccionar</option>
                              {ODONTOGRAM_CONDITIONS.map((c) => (
                                <option key={c} value={c}>
                                  {c}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="of-settings-actions" style={{ marginTop: 10 }}>
                            <button
                              onClick={() => assignCondition(row.id)}
                              disabled={savingCondition}
                              className="of-settings-button of-settings-button--success"
                            >
                              {savingCondition ? "Guardando..." : "Guardar"}
                            </button>

                            <button
                              onClick={() => {
                                setAssigningConditionId(null)
                                setSelectedCondition("")
                              }}
                              className="of-settings-button"
                            >
                              Cancelar
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                ])}
                emptyLabel="Sin tratamientos registrados."
              />
            )}
          </div>
        </div>
      )}
    </div>
  )
}
